import { ValueFormatterFunc, ValueFormatterParams } from "ag-grid-community";
import {
    DisplayerArgs,
    cellRendererDisplayers,
    FloatDisplayerA,
    IntegerDisplayerA,
    DatetimeLocaleDisplayerA,
    StringDisplayerA,
    ObjDisplayerA,
    NumericAffixA,
} from "./DFWhole";
import * as _ from "lodash-es";

import { HistogramCell } from "./HistogramCell";
import { Base64PNGDisplayer, LinkCellRenderer, SVGDisplayer } from "./OtherRenderers";
import { CellRendererArgs, FormatterArgs } from "./DFWhole";
import { getChartCell } from "./ChartCell";

/*
  this code should all be unit tested and in examples. Examples will
  show potential developers how this behaves. Examples should be made
  inside of AG-Grid, and independently.
  */

export const basicIntFormatter = new Intl.NumberFormat("en-US", {
    minimumFractionDigits: 0,
    maximumFractionDigits: 3,
});

export const getStringFormatter = (args: StringDisplayerA) => {
    const stringFormatter = (params: ValueFormatterParams): string => {
        const val = params.value;
        if (val && args.max_length && typeof val === "string") {
            try {
                return val.slice(0, args.max_length);
            } catch (e) {
                console.log("e", e, "val", val);
                return "";
            }
        }
        return val;
    };
    return stringFormatter;
};

const dictDisplayer = (val: Record<string, any>): string => {
    const objBody = _.map(val, (value, key) => `'${key}': ${objDisplayer(value)}`).join(",");
    return `{ ${objBody} }`;
};

export const isValidDate = (possibleDate: any): boolean => {
    if (_.isDate(possibleDate) && isFinite(possibleDate.getTime())) {
        return true;
    }
    return false;
};
const DEFAULT_DATE_FORMAT: Intl.DateTimeFormatOptions = {
    year: "numeric",
    month: "numeric",
    day: "numeric",
    hour: "numeric",
    minute: "numeric",
    second: "numeric",
    hour12: false,
};

export const dateDisplayerDefault = (d: Date): string => {
    const fullStr = d.toLocaleDateString("en-CA", DEFAULT_DATE_FORMAT);
    const [dateStr, timeStr] = fullStr.split(",");
    const retVal = `${dateStr} ${timeStr}`;
    return retVal;
};
const objDisplayer = (val: any | any[]): string => {
    if (val === undefined || val === null) {
        return "None";
    } else if (_.isArray(val)) {
        return `[ ${val.map(objDisplayer).join(", ")}]`;
    } else if (_.isBoolean(val)) {
        return boolDisplayer(val);
    } else if (_.isObject(val)) {
        return dictDisplayer(val);
    } else {
        return val.toString();
    }
    return val;
};

export const getObjectFormatter = (fArgs: ObjDisplayerA) => {
    const objFormatter = (params: ValueFormatterParams): string => {
        const val = params.value;
        const fullString = objDisplayer(val);
        if (fArgs.max_length) {
            return fullString.slice(0, fArgs.max_length);
        } else {
            return fullString;
        }
    };
    return objFormatter;
};
export const objFormatter = getObjectFormatter({ displayer: "obj" });

export const boolDisplayer = (val: boolean) => {
    if (val === true) {
        return "True";
    } else if (val === false) {
        return "False";
    }
    return "";
};

export const booleanFormatter = (params: ValueFormatterParams): string => {
    const val = params.value;
    return boolDisplayer(val);
};
const getIntegerFormatter = (hint: IntegerDisplayerA) => {
    const commas = Math.floor(hint.max_digits / 3);
    const totalWidth = commas + hint.max_digits;

    const formatter = new Intl.NumberFormat("en-US");
    const numericFormatter = (params: ValueFormatterParams): string => {
        const val = params.value;
        if (val === null) {
            return "";
        } else if(val === undefined) {
            return ""
        }
        return formatter.format(params.value).padStart(totalWidth, " ");
    };
    return numericFormatter;
};
export const getFloatFormatter = (hint: FloatDisplayerA) => {
    const floatFormatter = new Intl.NumberFormat("en-US", {
        minimumFractionDigits: hint.min_fraction_digits,
        maximumFractionDigits: hint.max_fraction_digits,
    });
    return (params: ValueFormatterParams): string => {
        if (params.value === null || params.value === undefined) {
            return "";
        }

        const res: string = floatFormatter.format(params.value);
        if (!_.includes(res, ".")) {
            const padLength = res.length + hint.max_fraction_digits + 1;
            return res.padEnd(padLength);
        } else {
            const fracPart = res.split(".")[1];
            const padLength = hint.max_fraction_digits - fracPart.length + res.length;
            return res.padEnd(padLength);
        }
    };
};

export const getDatetimeFormatter = (colHint: DatetimeLocaleDisplayerA) => {
    return (params: ValueFormatterParams): string => {
        const val = params.value;
        if (val === null || val === undefined) {
            return "";
        }
        const d = new Date(val);
        if (!isValidDate(d)) {
            return "";
        }
        return d.toLocaleDateString(colHint.locale, colHint.args);
    };
};

const compactFormatter = new Intl.NumberFormat("en", { notation: "compact", maximumFractionDigits: 1 });

export const getCompactNumberFormatter = () => {
    return (params: ValueFormatterParams): string => {
        if (params.value === null || params.value === undefined) return "";
        return compactFormatter.format(params.value);
    };
};

/**
 * Format a duration string into a human-readable representation like "1d 2h 3m 4.5s".
 * Accepts:
 *   - ISO 8601: "P1DT2H3M4.5S"
 *   - Pandas timedelta: "1 days 02:03:04", "0 days 00:30:00.500000"
 */
export const formatDuration = (raw: string): string => {
    let days = 0, hours = 0, minutes = 0, seconds = 0;

    // Try ISO 8601 first: P1DT2H3M4.5S
    const iso = raw.match(
        /^P(?:(\d+)D)?T(?:(\d+)H)?(?:(\d+)M)?(?:([\d.]+)S)?$/
    );
    if (iso) {
        days = parseInt(iso[1] || "0", 10);
        hours = parseInt(iso[2] || "0", 10);
        minutes = parseInt(iso[3] || "0", 10);
        seconds = parseFloat(iso[4] || "0");
    } else {
        // Try pandas timedelta: "N days HH:MM:SS" or "N days HH:MM:SS.ffffff"
        const pd = raw.match(
            /^(-?\d+)\s+days?\s+(\d{2}):(\d{2}):(\d{2}(?:\.\d+)?)$/
        );
        if (pd) {
            days = Math.abs(parseInt(pd[1], 10));
            hours = parseInt(pd[2], 10);
            minutes = parseInt(pd[3], 10);
            seconds = parseFloat(pd[4]);
        } else {
            return raw; // unrecognized format
        }
    }

    if (days === 0 && hours === 0 && minutes === 0 && seconds === 0)
        return "0s";

    const parts: string[] = [];
    if (days > 0) parts.push(`${days}d`);
    if (hours > 0) parts.push(`${hours}h`);
    if (minutes > 0) parts.push(`${minutes}m`);
    if (seconds > 0) {
        if (seconds < 0.001) {
            parts.push(`${Math.round(seconds * 1e6)}µs`);
        } else if (seconds < 1) {
            const ms = seconds * 1000;
            parts.push(ms === Math.floor(ms) ? `${ms}ms` : `${ms.toFixed(1)}ms`);
        } else {
            parts.push(
                seconds === Math.floor(seconds)
                    ? `${seconds}s`
                    : `${seconds.toFixed(3)}s`
            );
        }
    }
    return parts.join(" ");
};

/** @deprecated Use formatDuration instead */
export const formatIsoDuration = formatDuration;

export const getDurationFormatter = () => {
    return (params: ValueFormatterParams): string => {
        const val = params.value;
        if (val === null || val === undefined) return "";
        if (typeof val === "string") return formatDuration(val);
        // If it's a number (milliseconds), format directly
        if (typeof val === "number") {
            const totalMs = Math.abs(val);
            if (totalMs === 0) return "0s";
            const sign = val < 0 ? "-" : "";
            const days = Math.floor(totalMs / 86400000);
            const hours = Math.floor((totalMs % 86400000) / 3600000);
            const mins = Math.floor((totalMs % 3600000) / 60000);
            const secs = (totalMs % 60000) / 1000;
            const parts: string[] = [];
            if (days > 0) parts.push(`${days}d`);
            if (hours > 0) parts.push(`${hours}h`);
            if (mins > 0) parts.push(`${mins}m`);
            if (secs > 0) {
                parts.push(
                    secs === Math.floor(secs)
                        ? `${secs}s`
                        : `${secs.toFixed(3)}s`
                );
            }
            return sign + parts.join(" ");
        }
        return String(val);
    };
};

export const defaultDatetimeFormatter = (params: ValueFormatterParams): string => {
    const val = params.value;
    if (val === null || val === undefined) {
        return "";
    }
    const d = new Date(val);
    if (!isValidDate(d)) {
        return "";
    }
    return dateDisplayerDefault(d);
};

// Wrap a formatter with prefix/suffix text. The affix goes inside any
// alignment padding so padded columns stay aligned.
export const withAffix = (
    base: ValueFormatterFunc<unknown>,
    { prefix = "", suffix = "" }: NumericAffixA,
): ValueFormatterFunc<unknown> => {
    if (!prefix && !suffix) {
        return base;
    }
    return (params) => {
        const formatted = base(params);
        if (!formatted || typeof formatted !== "string") {
            return formatted;
        }
        const [, lead, body, trail] = formatted.match(/^(\s*)(.*?)(\s*)$/s)!;
        return `${lead}${prefix}${body}${suffix}${trail}`;
    };
};

export function getFormatter(fArgs: FormatterArgs): ValueFormatterFunc<unknown> {
    return withAffix(getBaseFormatter(fArgs), fArgs as NumericAffixA);
}

function getBaseFormatter(fArgs: FormatterArgs): ValueFormatterFunc<unknown> {
    switch (fArgs.displayer) {
        case "integer":
            return getIntegerFormatter(fArgs);
        case "string":
            return getStringFormatter(fArgs);
        case "datetimeDefault":
            return defaultDatetimeFormatter;
        case "datetimeLocaleString":
            return getDatetimeFormatter(fArgs);
        case "float":
            return getFloatFormatter(fArgs);
        case "boolean":
            return booleanFormatter;
        case "obj":
            return getObjectFormatter(fArgs);
        case "compact_number":
            return getCompactNumberFormatter();
        case "duration":
            return getDurationFormatter();
        default:
            return getStringFormatter({ displayer: "string" });
    }
}

/*
{
  cellRenderer: 'agCheckboxCellRenderer',
  cellRendererParams: {   disabled: true}
  }
  */
export function getCellRenderer(crArgs: CellRendererArgs) {
    switch (crArgs.displayer) {
        case "histogram":
            return HistogramCell;
        case "chart":
            return getChartCell(crArgs)
        case "linkify":
            return LinkCellRenderer;
        case "Base64PNGImageDisplayer":
            return Base64PNGDisplayer;
        case "boolean_checkbox":
            return "agCheckboxCellRenderer";
        case "SVGDisplayer":
            return SVGDisplayer;
    }
}

export function getFormatterFromArgs(dispArgs: DisplayerArgs) {
    if (_.includes(cellRendererDisplayers, dispArgs.displayer)) {
        return undefined;
    }
    const fArgs = dispArgs as FormatterArgs;
    return getFormatter(fArgs);
}
